/*
 * @Description: 注册主页面
 * @Author: kadenwang@tencent.com
 * @Date: 2021-11-15 18:03:46
 * @LastEditors: kadenwang@tencent.com
 * @LastEditTime: 2022-07-01 17:05:06
 */

import { getLoginCgiHost } from '@tencent/syzs-login-sdk-utils';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { BaseModel, Button, Select, Toast } from '../../component';
import i18n from '../../i18n';
import { addMobileClassName, fill2Zero, LoginRequest, postMessageToParent } from '../../lib';
import './RegisterMain.less';


export interface IFormKey {
  year: number;
  month: number;
  date: number;
  country: string;
}
export interface IOptions {
  value: string;
  label: string;
}

const generateDateInfo = (key: string, data: FieldValues) => {
  const temp: IOptions[] = [];
  const currDate = new Date();
  const { month, year } = data;

  switch (key) {
    case 'year':
      for (let index = 1900; index < new Date().getFullYear() + 1; index++) {
        temp.push({
          value: index.toString(),
          label: index.toString(),
        });
      }

      temp.reverse();
      break;
    case 'month': {
      const getMaxMonth = () => (currDate.getFullYear() === +year ? currDate.getMonth() + 1 : 12);

      for (let index = 1; index <= getMaxMonth(); index++) {
        temp.push({
          value: index.toString(),
          label: index.toString(),
        });
      }
    }

      break;
    case 'date': {
      // 判断最多可以展示的日期
      const getMaxDate = () => (currDate.getFullYear() === +year
        && currDate.getMonth() + 1 === +month ? currDate.getDate() : 0);

      const lastDayOfMonth = new Date(year, month, getMaxDate()).getDate();

      for (let i = 1; i <= lastDayOfMonth; i++) {
        temp.push({
          value: i.toString(),
          label: i.toString(),
        });
      }
    }

      break;
    default:
      break;
  }

  return temp;
};

export enum ISaveUserResultCode {
  'USER_ALREADY_EXISTS' = 20006,
  'USER_IS_TOO_YOUNG' = 20004,
  'LOGIN_SUCCESS' = 0,
}

export interface ISaveUser {
  birthDay: string;
  countryCode: number;
}

export const saveUser = async (params: ISaveUser) => {
  const { birthDay, countryCode } = params;

  const userInfoTemp = LoginRequest.getLoginInfolocalStorage();

  if (!userInfoTemp) return '';

  const { token_info, userinfo } = userInfoTemp;
  const { login_type } = userinfo;

  const result = await LoginRequest.request(`${getLoginCgiHost()}/v3/save_user`, {
    token_info,
    userinfo: {
      date_of_birth: birthDay,
      login_type,
      register_country_code: countryCode,
    },
  });

  const { status_code, data, msg } = result;

  switch (status_code) {
    case ISaveUserResultCode.USER_ALREADY_EXISTS:
    case ISaveUserResultCode.LOGIN_SUCCESS:
      LoginRequest.setLoginInfolocalStorage(data);
      LoginRequest.setLoginCookie();

      await postMessageToParent('LOGIN_SUCCESS', {
        userInfo: LoginRequest.getUserInfoAdapter(),
        rawInfo: userInfoTemp,
      });

      window.report(`syzs_login_v2_login_success_${login_type}`);

      window.close();
      return '';
    case ISaveUserResultCode.USER_IS_TOO_YOUNG:
      return 'USER_IS_TOO_YOUNG';
    default:
      Toast.error(`Failed to register user: ${status_code}`);
      window.aegis.error(`Failed to register status_code: ${status_code} msg ${msg}`);
      return '';
  }
};

export const requestCountry = async () => {
  const result = await LoginRequest.request(`${getLoginCgiHost()}/v3/all_countries`, {});
  const countriesItem: IOptions[] = [];
  const { data, ip_country = {} } = result;
  const defaultKey = Object.keys(ip_country)[0];

  Object.keys(data).forEach((item) => {
    countriesItem.push({ label: data[item], value: item });
  });

  return { countriesItem, defaultKey };
};

const RegisterMain = () => {
  const { control, getValues, watch, setValue } = useForm();
  const [yearData, setYearData] = useState<IOptions[]>([]);
  const [monthData, setMonthData] = useState<IOptions[]>([]);
  const [dateData, setDateData] = useState<IOptions[]>([]);
  const [countries, setCountries] = useState<IOptions[]>([]);
  const watchFields = watch();
  const { year, month, date, country } = watchFields;
  const [userTooYoung, setUserTooYoung] = useState(false);

  const handleClickSubmit = useCallback(
    async () => {
      const status = await saveUser({ birthDay: `${year}-${fill2Zero(month)}-${fill2Zero(date)}`, countryCode: Number(country) });

      if (status === 'USER_IS_TOO_YOUNG') {
        setUserTooYoung(true);
      }
    },
    [year, month, date, country],
  );

  useEffect(() => {
    if (!year) return;
    // 如果年变了 需要重置月份和日期
    setValue('date', undefined);
    setValue('month', undefined);
    // 生成新的月份列表
    setMonthData(generateDateInfo('month', getValues()));
  }, [year, getValues, setValue]);

  useEffect(() => {
    if (!month) return;
    // 月份变了需要重置日期
    setValue('date', undefined);
    setDateData(generateDateInfo('date', getValues()));
  }, [getValues, month, setValue]);

  useEffect(() => {
    setUserTooYoung(false);
  }, [date, year, month, country]);

  useEffect(() => {
    // 初始化年份信息
    setYearData(generateDateInfo('year', getValues()));
  }, [getValues]);

  useEffect(() => {
    const init = async () => {
      const { countriesItem, defaultKey } = await requestCountry();

      setCountries(countriesItem);

      if (defaultKey) {
        setValue('country', defaultKey);
      }
    };

    void init();
  }, [setValue]);

  useEffect(() => {
    window.report('register_main_view');
  });

  return (
    <div className={addMobileClassName('login-v2-main')}>
      <BaseModel
        showLeftModule
        showClose={false}
      >
        <div className="login-v2-register-main">
          <p className="title">{i18n.l('InformationRequired')}</p>
          <p className="sub-title">{i18n.l('proceed')}</p>
          <div className="form-info">
            <p className="form-title">{i18n.l('DateOfBirth')}</p>
            <div className="select-year datepicker">
              <Select
                name="year"
                control={control}
                options={yearData}
                defaultValue="Year"
              />
            </div>
            <div className="select-month datepicker">
              <Select
                name="month"
                control={control}
                options={monthData}
                defaultValue="Month"
              />
            </div>
            <div className="select-date datepicker">
              <Select
                name="date"
                control={control}
                options={dateData}
                defaultValue="Day"
              />
            </div>
            <p className="form-title">{i18n.l('CountryRegion')}</p>
            <div className="select-country">
              <Select
                name="country"
                control={control}
                options={countries}
                defaultValue="Country / Region"
                useFastSelect
              />
            </div>
            <div className="confim-button">
              <Button disable={!(year && month && date && country)} onClick={handleClickSubmit} />
            </div>
            {userTooYoung && (
              <div className="error-msg">
                {i18n.l('UnderAgeRequirement')}
              </div>
            )}
          </div>
        </div>
      </BaseModel>
    </div>
  );
};

export default RegisterMain;
